import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { Clock, Handshake, Loader2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { useOwnedOrganizations } from "@/lib/owned-organizations";
import { clearPendingConnectInvite } from "@/lib/pending-connect-invite";
import { useFluxoNovoPedido } from "../FluxoNovoPedido";

const primario =
  "inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50";
const secundario = "inline-flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted";

/** "serviço:material" (ou só "serviço") do catálogo público → a escolha inicial do pedido. */
function servicoDaLinha(linha?: string) {
  if (!linha) return undefined;
  const [serviceCategoryId, materialOptionId] = linha.split(":");
  return serviceCategoryId ? { serviceCategoryId, materialOptionId: materialOptionId || null } : undefined;
}

/**
 * Quem chegou pelo link público do catálogo de um laboratório, já dentro do app: pede a conexão
 * (o laboratório aceita em Cadastros) ou, já conectado, abre o pedido com o laboratório — e, se
 * veio do "Pedir" de uma linha, com o serviço e o material já escolhidos.
 */
export function ConectarPeloCatalogoDialog({
  codigo,
  linha,
  onFechar,
}: {
  codigo: string;
  linha?: string;
  /** descartar = limpa o pendente; sem isso, ele volta no próximo acesso (ex.: trocar de organização). */
  onFechar: (descartar: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const { user } = useAuth();
  const { clinics } = useOwnedOrganizations();
  const [aberto, setAberto] = useState(true);
  const [enviado, setEnviado] = useState(false);
  const [todas, setTodas] = useState(false);
  const fluxo = useFluxoNovoPedido();
  const conexao = trpc.catalogoPublico.conexao.useQuery({ codigo }, { retry: false });
  const servico = servicoDaLinha(linha);

  const conectar = trpc.catalogoPublico.conectar.useMutation({
    onSuccess: (r) => {
      const pulados = [...r.jaConectadas, ...r.jaPendentes];
      if (pulados.length) toast.info(`Sem pedido para ${pulados.join(", ")}: já conectada ou com pedido pendente.`);
      utils.prosthesis.lab.connectionRequests.listOutgoing.invalidate();
      utils.catalogoPublico.conexao.invalidate();
      setEnviado(true);
    },
    onError: (e) => toast.error(e.message),
  });

  const fechar = (descartar: boolean) => {
    setAberto(false);
    onFechar(descartar);
  };

  // O pedido abre fora deste diálogo: o pendente já foi usado, mas o componente fica montado pro
  // fluxo de novo pedido continuar na tela.
  const fazerPedido = (labId: string) => {
    clearPendingConnectInvite();
    setAberto(false);
    fluxo.abrirComLaboratorio({ labId, servico });
  };

  const d = conexao.data;
  const nome = d?.laboratorio.nome ?? "o laboratório";
  const varias = clinics.length > 1;

  let corpo: ReactNode;
  if (conexao.isLoading) corpo = <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-zinc-400" />;
  else if (conexao.error || !d) {
    corpo = (
      <Rodape>
        <p className="flex-1 text-sm text-zinc-600 dark:text-muted-foreground">{conexao.error?.message ?? "Este catálogo não está disponível."}</p>
        <button type="button" className={secundario} onClick={() => fechar(true)}>Fechar</button>
      </Rodape>
    );
  } else if (enviado || d.estado === "pendente") {
    corpo = (
      <Aviso icone={<Clock className="h-5 w-5" />}>
        {enviado ? "Pedido de conexão enviado." : "Seu pedido de conexão está aguardando o laboratório."} Quando {nome} aceitar, você
        já pode pedir próteses para ele{servico ? " — o serviço que você escolheu no catálogo fica para o primeiro pedido" : ""}.
        <Rodape><button type="button" className={primario} onClick={() => fechar(true)}>Entendi</button></Rodape>
      </Aviso>
    );
  } else if (d.estado === "conectado" && d.labId) {
    const labId = d.labId;
    corpo = (
      <Aviso icone={<Handshake className="h-5 w-5" />}>
        Vocês já estão conectados. O pedido usa os preços combinados com a sua clínica.
        <Rodape>
          <button type="button" className={secundario} onClick={() => fechar(true)}>Agora não</button>
          <button type="button" className={primario} onClick={() => fazerPedido(labId)}>Fazer o pedido</button>
        </Rodape>
      </Aviso>
    );
  } else if (!d.ativaEhClinica) {
    corpo = (
      <Aviso icone={<Handshake className="h-5 w-5" />}>
        Para conectar com {nome}, troque para uma clínica no seletor de organização, no topo da tela.
        <Rodape>
          <Link to="/organizacoes" className={secundario} onClick={() => fechar(false)}>Ver organizações</Link>
          <button type="button" className={secundario} onClick={() => fechar(true)}>Descartar</button>
        </Rodape>
      </Aviso>
    );
  } else if (!d.podeConectar) {
    corpo = (
      <Aviso icone={<Handshake className="h-5 w-5" />}>
        Quem cuida dos parceiros da clínica precisa pedir a conexão com {nome}. Peça a essa pessoa para abrir o link do catálogo.
        <Rodape><button type="button" className={primario} onClick={() => fechar(true)}>Entendi</button></Rodape>
      </Aviso>
    );
  } else {
    corpo = (
      <Aviso icone={<Handshake className="h-5 w-5" />}>
        Conecte {varias && todas ? "as suas clínicas" : user?.activeOrganization?.name ?? "a sua clínica"} com {nome} para enviar pedidos de prótese.
        O laboratório recebe o pedido de conexão e aceita.
        {varias && (
          <label className="mt-3 flex items-start gap-2 rounded-lg border border-zinc-200 px-3 py-2 dark:border-border">
            <input type="checkbox" checked={todas} onChange={(e) => setTodas(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-brand-600 dark:border-border" />
            <span className="text-[12px] text-zinc-700 dark:text-foreground">Pedir por todas as minhas clínicas ({clinics.length})</span>
          </label>
        )}
        <Rodape>
          <button type="button" className={secundario} onClick={() => fechar(true)}>Agora não</button>
          <button type="button" className={primario} disabled={conectar.isPending} onClick={() => conectar.mutate({ codigo, todasAsMinhasClinicas: varias && todas })}>
            {conectar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Pedir conexão
          </button>
        </Rodape>
      </Aviso>
    );
  }

  return (
    <>
      <Dialog open={aberto} onOpenChange={(v) => !v && fechar(false)}>
        <DialogContent className="max-w-md border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
          <DialogHeader>
            <div className="flex items-center gap-3">
              {d?.laboratorio.logoUrl && <img src={d.laboratorio.logoUrl} alt="" className="h-10 w-10 rounded-lg object-contain" />}
              <div className="min-w-0 text-left">
                <DialogTitle>{d ? d.laboratorio.nome : "Catálogo do laboratório"}</DialogTitle>
                <DialogDescription>Você veio pelo catálogo deste laboratório.</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          {corpo}
        </DialogContent>
      </Dialog>
      {fluxo.elementos}
    </>
  );
}

function Aviso({ icone, children }: { icone: ReactNode; children: ReactNode }) {
  return (
    <div className="flex gap-3 text-sm text-zinc-700 dark:text-foreground">
      <span className="mt-0.5 shrink-0 text-brand-600 dark:text-primary">{icone}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function Rodape({ children }: { children: ReactNode }) {
  return <div className="mt-4 flex items-center justify-end gap-2">{children}</div>;
}
